import { statusKind, STATUS_LOOK } from '../dashboardTheme.js'
import { countryInfo } from '../countries.js'
import { sector } from './kit.js'
import Flag from './Flag.jsx'

/**
 * StatusBoard — a table of statuses (Ready / Partly / Missing, Fresh / Stale…) read for the
 * reader: how much is in place overall, on a gauge; then each row with its own bar and, in words,
 * exactly what it lacks — worst first, because the point of the screen is to find the gaps. With a
 * single status column the rows are grouped under their status instead.
 *
 * The product's own words are used throughout ("Ready", "Missing"); the colours are the status
 * colours the table pills use, and every colour has its word beside it.
 */
const KINDS = ['good', 'warn', 'bad', 'mute']

/** The commonest word a table uses for each kind of status: {good: 'Ready', bad: 'Missing', …}. */
function wordsFor(cells) {
  const count = {}
  cells.forEach(v => {
    const k = statusKind(v)
    if (!k) return
    const w = String(v).trim()
    count[k] = count[k] || {}
    count[k][w] = (count[k][w] || 0) + 1
  })
  const out = {}
  Object.entries(count).forEach(([k, m]) => { out[k] = Object.entries(m).sort((a, b) => b[1] - a[1])[0][0] })
  return out
}

function Gauge({ counts, words }) {
  const counted = counts.good + counts.warn + counts.bad
  const R = 92, r0 = 64, cx = 110, cy = 104
  let a = -Math.PI / 2
  const pct = counted ? Math.round((counts.good / counted) * 100) : 0
  return (
    <svg width="220" height="122" role="img" aria-label={`${pct}% ${words.good || 'ready'}`} className="shrink-0">
      <path d={sector(cx, cy, r0, R, -Math.PI / 2, Math.PI / 2)} fill="#F1F0EC" />
      {['good', 'warn', 'bad'].map(k => {
        if (!counts[k] || !counted) return null
        const a0 = a, a1 = a + (Math.PI * counts[k]) / counted
        a = a1
        return <path key={k} d={sector(cx, cy, r0, R, a0 + 0.01, a1 - 0.01)} fill={STATUS_LOOK[k].dot} className="pt-rise" />
      })}
      <text x={cx} y={cy - 14} textAnchor="middle" fontSize="30" fontWeight="800" fill="#111827">{pct}%</text>
      <text x={cx} y={cy + 4} textAnchor="middle" fontSize="12" fontWeight="600" fill="#4B5563">{words.good || 'Ready'}</text>
    </svg>
  )
}

export default function StatusBoard({ columns, rows, statusCols, nameCol }) {
  const name = (r) => String(r[nameCol] ?? '').trim() || '—'
  const all = rows.flatMap(r => statusCols.map(c => r[c]))
  const words = wordsFor(all)
  const counts = { good: 0, warn: 0, bad: 0, mute: 0 }
  all.forEach(v => { const k = statusKind(v); if (k) counts[k]++ })
  const head = (c) => String(columns?.[c] ?? `Column ${c + 1}`)

  const scored = rows.map(r => {
    const by = { good: [], warn: [], bad: [], mute: [] }
    statusCols.forEach(c => { const k = statusKind(r[c]); if (k) by[k].push(head(c)) })
    const counted = by.good.length + by.warn.length + by.bad.length
    return { r, by, counted, ready: counted ? by.good.length / counted : 0 }
  }).sort((a, b) => b.by.bad.length - a.by.bad.length || b.by.warn.length - a.by.warn.length || a.ready - b.ready)

  const Chip = ({ k, n }) => (
    <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold" style={{ background: STATUS_LOOK[k].bg, color: STATUS_LOOK[k].fg }}>
      <span className="w-2 h-2 rounded-full" style={{ background: STATUS_LOOK[k].dot }} />{words[k]} <span className="tabular-nums">{n}</span>
    </span>
  )

  return (
    <div className="rounded-2xl border border-gray-200 bg-white shadow-xs p-4 sm:p-5">
      <div className="flex flex-wrap items-center gap-5">
        <Gauge counts={counts} words={words} />
        <div className="min-w-0 flex-1">
          <p className="text-sm text-gray-700">
            <b className="text-gray-900">{counts.good}</b> of <b className="text-gray-900">{counts.good + counts.warn + counts.bad}</b> items are {String(words.good || 'ready').toLowerCase()},
            across {rows.length} {rows.length === 1 ? 'row' : 'rows'}{statusCols.length > 1 ? ` and ${statusCols.length} kinds of data` : ''}.
          </p>
          <div className="mt-2.5 flex flex-wrap gap-2">
            {KINDS.filter(k => counts[k]).map(k => <Chip key={k} k={k} n={counts[k]} />)}
          </div>
          {statusCols.length > 1 && (
            <div className="mt-4 grid gap-x-5 gap-y-2 sm:grid-cols-2">
              {statusCols.map(c => {
                const cells = rows.map(r => statusKind(r[c]))
                const good = cells.filter(k => k === 'good').length
                const n = cells.filter(k => k && k !== 'mute').length
                return (
                  <div key={c} className="text-xs">
                    <div className="flex justify-between gap-2"><span className="font-semibold text-gray-700 truncate">{head(c)}</span><span className="tabular-nums text-gray-500">{good}/{n}</span></div>
                    <div className="mt-1 h-1.5 rounded-full bg-gray-100 overflow-hidden" aria-hidden="true">
                      <div className="h-full rounded-full pt-grow" style={{ width: `${n ? (good / n) * 100 : 0}%`, background: STATUS_LOOK.good.dot }} />
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {statusCols.length > 1 ? (
        <div className="mt-5 border-t border-gray-100 pt-4">
          <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-2.5">Most gaps first</p>
          <ul className="space-y-2.5">
            {scored.map(({ r, by, counted }, i) => (
              <li key={i} className="grid gap-x-4 gap-y-1 sm:grid-cols-[minmax(9rem,14rem)_minmax(0,1fr)] items-start">
                <span className="flex items-center gap-2 text-sm font-semibold text-gray-800 min-w-0">
                  <Flag iso2={countryInfo(name(r))?.iso2} size={16} /><span className="truncate">{name(r)}</span>
                </span>
                <div className="min-w-0">
                  <div className="mt-1.5 flex h-2.5 rounded-full overflow-hidden bg-gray-100 gap-[2px]" aria-hidden="true">
                    {['good', 'warn', 'bad'].map(k => by[k].length > 0 && (
                      <span key={k} className="h-full pt-grow" style={{ width: `${(by[k].length / Math.max(1, counted)) * 100}%`, background: STATUS_LOOK[k].dot }} />
                    ))}
                  </div>
                  <p className="mt-1 text-[11.5px] text-gray-600">
                    {by.bad.length > 0 && <><b style={{ color: STATUS_LOOK.bad.fg }}>{words.bad}:</b> {by.bad.join(', ')}</>}
                    {by.bad.length > 0 && by.warn.length > 0 && <span className="text-gray-300"> · </span>}
                    {by.warn.length > 0 && <><b style={{ color: STATUS_LOOK.warn.fg }}>{words.warn}:</b> {by.warn.join(', ')}</>}
                    {by.bad.length === 0 && by.warn.length === 0 && <span style={{ color: STATUS_LOOK.good.fg }} className="font-semibold">All {String(words.good || 'ready').toLowerCase()}</span>}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="mt-5 border-t border-gray-100 pt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {['bad', 'warn', 'good', 'mute'].filter(k => counts[k]).map(k => (
            <div key={k}>
              <p className="text-xs font-bold mb-2 flex items-center gap-1.5" style={{ color: STATUS_LOOK[k].fg }}>
                <span className="w-2 h-2 rounded-full" style={{ background: STATUS_LOOK[k].dot }} />{words[k]} ({counts[k]})
              </p>
              <div className="flex flex-wrap gap-1.5">
                {rows.filter(r => statusKind(r[statusCols[0]]) === k).map((r, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-gray-50 px-2 py-1 text-xs text-gray-800">
                    <Flag iso2={countryInfo(name(r))?.iso2} size={13} />{name(r)}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
